import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type CreateEntitySuggestionRequest,
  type CreateEntitySuggestionResponse,
  type GetProcessDraftResponse,
  type ShowProcessResponse
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import {
  entitySuggestionKeys,
  processDraftKeys,
  processKeys,
  recordingKeys,
  taskKeys
} from '../query-keys';

/**
 * Client-only extension of the create request: process-anchored cache
 * seeding for node-anchored creates. A start-event-picker create carries the
 * start event NODE's id in `sourceRecordingId` (issue 11), so the wire
 * payload no longer names the process — `seedProcessId` carries it for the
 * cache seed below and is stripped before the POST.
 */
type CreateEntitySuggestionVariables = CreateEntitySuggestionRequest & {
  seedProcessId?: string;
  /**
   * Draft-editor origin: `sourceRecordingId` is then a draft node's
   * `stableStepId` and the process comes from the route, not the body.
   */
  draftProcessId?: string;
};

/**
 * Create a new EntitySuggestion (system or team) attached to a recording.
 * The new entity does not exist until an admin approves — callers should NOT
 * auto-select on success. We invalidate `recording`, `tasks` and
 * `entity-suggestion` so the "Vorgeschlagen" placeholder and the approver
 * review list pick the new suggestion up immediately.
 */
export function useCreateEntitySuggestion(draftProcessId?: string) {
  const qc = useQueryClient();
  return useMutation({
    // A draft-scoped create counts as a pending write to that draft.
    mutationKey: draftProcessId ? processDraftKeys.mutations(draftProcessId) : undefined,
    mutationFn: async ({
      seedProcessId: _seedProcessId,
      draftProcessId,
      ...data
    }: CreateEntitySuggestionVariables): Promise<CreateEntitySuggestionResponse> => {
      const { data: response } = await apiClient.post<CreateEntitySuggestionResponse>(
        draftProcessId
          ? `/api/v2/entity-suggestion/draft/${draftProcessId}`
          : '/api/v2/entity-suggestion',
        data
      );
      return response;
    },
    onSuccess: (resp, variables) => {
      qc.invalidateQueries({ queryKey: taskKeys.all });
      qc.invalidateQueries({ queryKey: entitySuggestionKeys.all });
      // Step-recording sources: invalidate the recording namespace so the
      // picker sees the new suggestion. A node-anchored sourceRecordingId
      // (start event node) has no recording query — the invalidate no-ops.
      if (variables.sourceRecordingId) {
        qc.invalidateQueries({
          queryKey: recordingKeys.stepRecording(variables.sourceRecordingId)
        });
      }
      // Process-anchored seed: the explicit seedProcessId hint a
      // node-anchored create passes along.
      const seedProcessId = variables.seedProcessId;
      if (seedProcessId) {
        // Seed both the draft and the show-process caches with the new
        // suggestion. Both surfaces resolve a suggestion-typed ref's proposed
        // name from an `entitySuggestions[]` lookup, and in both the ref is
        // attached to its slot *after* this create resolves — so a plain
        // invalidate wouldn't help: show-process only returns suggestions
        // already referenced on the process, so the refetch would drop a
        // just-created, not-yet-attached one (the reassign "Zuweisen an" chip
        // would read "Kein Zuständiger"). Creating a suggestion changes nothing
        // else on either response, so seeding in place is exact, not optimistic.
        // Idempotent append — a later real refetch that already includes the
        // suggestion is a no-op. The spread stays at each concrete call site so
        // the response type is preserved.
        type EntitySuggestionList = ShowProcessResponse['entitySuggestions'];
        const withSuggestion = (list: EntitySuggestionList): EntitySuggestionList =>
          list.some((s) => s._id === resp.data._id) ? list : [...list, resp.data];

        qc.setQueryData<GetProcessDraftResponse | 'not-found'>(
          processDraftKeys.detail(seedProcessId),
          (prev) =>
            !prev || prev === 'not-found'
              ? prev
              : {
                  ...prev,
                  entitySuggestions: withSuggestion(prev.entitySuggestions)
                }
        );
        qc.setQueryData<ShowProcessResponse>(processKeys.detail(seedProcessId), (prev) =>
          !prev
            ? prev
            : {
                ...prev,
                entitySuggestions: withSuggestion(prev.entitySuggestions)
              }
        );
      }
    }
  });
}
